import React, { useEffect, useState } from "react";
import { Auth } from "aws-amplify";
// ANT DESIGN COMPONENTS
import { Modal, Input, Button } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateOpenModalTotpToken,
  updateTotpTokenIsValid,
  updateTotpToken,
} from "../../redux/authentication/authenticationSlice";
import {
  usePostMfaStepUpVerifyMutation,
  usePostMfaStepUpPasswordVerifyMutation,
} from "../../redux/mfa/mfaAPI";
// AUTH HELPER
import GETJwtToken, { storeOfflineSessionToken } from "../../redux/authentication/authentication";
// UTILS
import { styleDeleteButton } from "../../utils/utils";
// INTERFACES
import { RootState } from "../../app/store";

const TOTP_LENGTH = 6;

/**
 * Whether to route TOTP verification through the local
 * /api/mfa/step-up/verify endpoint (offline JWT) instead of Cognito's
 * Auth.signIn / Auth.confirmSignIn.
 *
 * Two ways the answer can be "yes":
 *   1. Build env explicitly says OFFLINE (REACT_APP_ENVIRONMENT === "OFFLINE",
 *      i.e. `npm run start:offline`).
 *   2. The user logged in via the Offline page, which writes an
 *      `offlineToken` into localStorage. This catches `npm run start:local`
 *      pointed at a localhost backend — there's no Cognito session there,
 *      so the cloud path's `Auth.signIn(username, password)` would error
 *      out and the TOTP gate would never close.
 *
 * Evaluated lazily inside the component so a token written after first
 * render (e.g. user logged in moments ago) is picked up on next open.
 */
function isOfflineMode(): boolean {
  if (process.env.REACT_APP_ENVIRONMENT === "OFFLINE") return true;
  try {
    return Boolean(localStorage.getItem("offlineToken"));
  } catch {
    // jsdom / SSR edge — fall back to env flag only.
    return false;
  }
}

/**
 * AuthenticationTOTP gates sensitive UI actions behind a real second-factor
 * verification.
 *
 * Two implementations selected at runtime by REACT_APP_ENVIRONMENT:
 *
 *   OFFLINE (local JWT mode):
 *     - User submits TOTP code → POST /api/mfa/step-up/verify
 *     - Backend verifies the code against the user's encrypted MFASecret
 *       (RFC 6238 TOTP) and issues a fresh JWT with `step_up_iat = now`
 *     - That JWT replaces localStorage.offlineToken so subsequent
 *       sensitive calls satisfy RequireFreshMFA's freshness window
 *
 *   CLOUD (Cognito mode):
 *     - User submits password + TOTP
 *     - Frontend calls Auth.signIn(username, password); on the resulting
 *       SOFTWARE_TOKEN_MFA challenge, calls Auth.confirmSignIn(code)
 *     - Cognito issues fresh tokens whose OIDC `auth_time` claim is the
 *       moment of MFA verification — RequireFreshMFA reads `auth_time`
 *       and the next sensitive call passes
 *
 * BOTH paths fail-closed: if verification fails, totpTokenIsValid stays
 * false and the sensitive action does NOT proceed. The prior implementation
 * unconditionally marked the token valid (security-theater).
 */
function AuthenticationTOTP() {
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  const [totpToken, setTotpToken] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { openModalTotpToken } = useSelector((state: RootState) => state.authentication);
  const { theme } = useSelector((state: RootState) => state.home);

  const [triggerStepUpVerify] = usePostMfaStepUpVerifyMutation();
  const [triggerStepUpPasswordVerify] = usePostMfaStepUpPasswordVerifyMutation();

  const { userLogged } = useSelector((state: RootState) => state.home);
  // Only explicit true means enrolled — undefined/false/null → not enrolled.
  // Null userLogged (still loading) defaults to true: TOTP path shown, server
  // returns 409 with a clear message, rather than silently switching modes.
  const mfaEnrolled = userLogged ? userLogged.mfa_enrolled === true : true;

  // showPasswordMode is true when: offline mode AND the user has not enrolled
  // TOTP. In this case the modal shows a password prompt that calls
  // /api/mfa/step-up/password-verify instead of the TOTP endpoint.
  const offline = isOfflineMode();
  const showPasswordMode = offline && !mfaEnrolled;

  const onClickCancel = () => {
    dispatch(updateOpenModalTotpToken(false));
  };

  const handleVerifySuccess = (code: string) => {
    // Mark the gate satisfied for the consumer's useEffect, then close.
    dispatch(updateTotpToken(code));
    dispatch(updateTotpTokenIsValid(true));
    dispatch(updateOpenModalTotpToken(false));
  };

  const verifyOfflineTotp = async () => {
    const authHeader = await GETJwtToken();
    const result = await triggerStepUpVerify({
      token: authHeader,
      body: { totp_code: totpToken },
    }).unwrap();
    if (!result || !result.step_up_token) {
      throw new Error("Server did not return a step-up token");
    }
    // Replace the cached access token so the next request to a sensitive
    // endpoint carries the freshly-minted step_up_iat claim. The TTL is
    // server-side (5 minutes) — RequireFreshMFA enforces freshness on the
    // backend, the frontend just stores the token until then.
    storeOfflineSessionToken(result.step_up_token);
  };

  const verifyOfflinePassword = async () => {
    const authHeader = await GETJwtToken();
    const result = await triggerStepUpPasswordVerify({
      token: authHeader,
      body: { password },
    }).unwrap();
    if (!result || !result.step_up_token) {
      throw new Error("Server did not return a step-up token");
    }
    storeOfflineSessionToken(result.step_up_token);
  };

  const verifyCloud = async () => {
    if (!password) {
      throw new Error("Password is required to re-verify with Cognito");
    }
    const current = await Auth.currentAuthenticatedUser();
    const username = current?.username || current?.attributes?.email;
    if (!username) {
      throw new Error("Could not determine your Cognito username");
    }
    const challenge = await Auth.signIn(username, password);
    if (challenge && challenge.challengeName === "SOFTWARE_TOKEN_MFA") {
      await Auth.confirmSignIn(challenge, totpToken, "SOFTWARE_TOKEN_MFA");
    }
    // After confirmSignIn the next Auth.currentSession() returns tokens
    // whose `auth_time` claim has advanced — RequireFreshMFA on the
    // backend will pass for ~5 minutes.
  };

  const handleSubmitConfirmTOTP = async () => {
    setErrorMessage("");
    if (showPasswordMode) {
      if (!password) {
        setErrorMessage("Password is required");
        return;
      }
    } else if (totpToken.length !== TOTP_LENGTH) {
      setErrorMessage(`Code must be ${TOTP_LENGTH} digits`);
      return;
    }
    setSubmitting(true);
    try {
      if (showPasswordMode) {
        await verifyOfflinePassword();
        handleVerifySuccess("");
      } else if (offline) {
        await verifyOfflineTotp();
        handleVerifySuccess(totpToken);
      } else {
        await verifyCloud();
        handleVerifySuccess(totpToken);
      }
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.warn("AuthenticationTOTP: verification failed", err?.status ?? err?.message);
      setErrorMessage("Verification failed. Check the code and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const onPressEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const canSubmit = showPasswordMode ? password.length > 0 : totpToken.length === TOTP_LENGTH;
    if (event.key === "Enter" && canSubmit && !submitting) {
      handleSubmitConfirmTOTP();
    }
  };

  useEffect(() => {
    if (openModalTotpToken) {
      setTotpToken("");
      setPassword("");
      setErrorMessage("");
      setSubmitting(false);
    }
  }, [openModalTotpToken]);

  const submitDisabled = showPasswordMode
    ? submitting || password.length === 0
    : submitting || totpToken.length !== TOTP_LENGTH || (!offline && password.length === 0);

  return (
    <Modal closable={false} onCancel={onClickCancel} open={openModalTotpToken} footer={null}>
      <div>
        <div className="text-center">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="generalStyles__warning generalStyles__deleteItemIcon"
          />
          {showPasswordMode ? (
            <>
              <p className="mt-3 drawer__text">{t("authentication.validatePasswordDescription")}</p>
              <Input.Password
                placeholder={t("general.offlinePassword")}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={onPressEnter}
                value={password}
                status="warning"
              />
            </>
          ) : (
            <>
              <p className="mt-3 drawer__text">
                {t("monitoring.validateTotpTokenDescription.description1")}
                <b className="generalStyles__warning">TOTP Token</b>.
                {t("monitoring.validateTotpTokenDescription.description2")}
                <b className="generalStyles__warning">Authy</b>
                {t("monitoring.validateTotpTokenDescription.description3")}
                <b className="generalStyles__warning">Google Authenticator</b>.
              </p>
              {!offline && (
                <Input.Password
                  placeholder="Your password"
                  onChange={(e) => setPassword(e.target.value)}
                  value={password}
                  status="warning"
                  style={{ marginBottom: "10px" }}
                />
              )}
              <Input
                placeholder="TOTP Token"
                onChange={(e) => setTotpToken(e.target.value)}
                onKeyDown={onPressEnter}
                value={totpToken}
                status="warning"
                maxLength={TOTP_LENGTH}
              />
            </>
          )}
          {errorMessage && (
            <p className="generalStyles__error" style={{ marginTop: "5px", marginBottom: "0px" }}>
              {errorMessage}
            </p>
          )}
        </div>
        <div className="generalStyles__flexEnd mt-4">
          <div>
            <Button
              htmlType="submit"
              className={styleDeleteButton(submitDisabled, theme)}
              onClick={handleSubmitConfirmTOTP}
              disabled={submitDisabled}
              loading={submitting}
            >
              {t("monitoring.validateTotpToken")}
            </Button>
            <Button onClick={onClickCancel} className="buttonStyle__4">
              {t("general.close")}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default AuthenticationTOTP;
